import React, { CSSProperties } from "react";
import { css } from "@emotion/react";

import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { SwarovskiRadio } from "../ui-elements/SwarovskiRadio";
import { CSS_MQ } from "../../../styles/breakpoints";
import { ActivatedFacets, FilterValue } from "./types";
import { T } from "../../T";

type OpenedFacetTabProps = {
  openedFilterTabByFilterKey: string;
  activatedFacets: ActivatedFacets;
  filterValues: Array<FilterValue>;
  onFilterValueClicked: (value: FilterValue) => Promise<void> | void;
  colorsCssMap: Record<string, string>;
  isChecked: (v: FilterValue) => boolean;
  inputType: "checkbox" | "radio";
  allowMultipleSelection: boolean;
};

export const OpenedFacetTab: ReactTFC<OpenedFacetTabProps> = props => {
  const atLeastOneChecked = !!props.filterValues.find(v => props.isChecked(v));

  function isActive(v: FilterValue): boolean {
    return v.count > 0;
  }

  return (
    <form css={formStyles} style={{ "--filters-count": props.filterValues.length } as CSSProperties}>
      {props.filterValues.map(v => {
        const active = isActive(v);
        const checked = props.isChecked(v);

        const customColorInput =
          props.openedFilterTabByFilterKey === "color"
            ? (typeof v.value === "string" && props.colorsCssMap[v.value]) || CSS_COLORS.Grey2
            : undefined;

        const onChange = active || checked ? () => props.onFilterValueClicked(v) : undefined;

        const isDisabled = !active || (!props.allowMultipleSelection && atLeastOneChecked && !checked);

        return (
          <SwarovskiRadio
            key={JSON.stringify({ ...v, checked })}
            type={props.inputType}
            attachStyles={css`
              label {
                padding: 8px 0;
              }
            `}
            customColorInput={customColorInput}
            labelProps={{
              className: active ? "available" : "not-available",
            }}
            inputProps={{
              value: v.value.toString(),
              disabled: isDisabled,
              defaultChecked: checked,
              onClick: onChange
                ? () => {
                    void onChange();
                  }
                : undefined,
            }}
          >
            <span css={labelStyles}>
              <T>{v.label}</T>
            </span>
          </SwarovskiRadio>
        );
      })}
    </form>
  );
};

const labelStyles = css`
  input[disabled] + & {
    color: ${CSS_COLORS.Grey3};
  }

  input:checked + & {
    color: ${CSS_COLORS.Green};
  }
`;

const formStyles = css`
  display: grid;

  padding-top: 2px;
  padding-bottom: 2px;

  ${CSS_MQ.atMostSm} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-auto-flow: row;
    grid-gap: 6px;

    margin-bottom: 12px;
  }

  ${CSS_MQ.atLeastMd} {
    grid-template-rows: repeat(min(var(--filters-count), 3), minmax(0, 1fr));
  }

  ${CSS_MQ.atLeastLg} {
    grid-template-rows: repeat(min(var(--filters-count), 3), minmax(0, 1fr));
  }

  ${CSS_MQ.atLeastMd} {
    margin-top: 10px;

    grid-auto-flow: column;
    grid-row-gap: 0; // use padding to improve clickability
    grid-column-gap: 20px;

    border-left: 2px solid ${CSS_COLORS.Green};
    padding-left: 20px;
  }
`;
